import * as d3 from 'd3';
import { defineTexturedGlassPatterns } from './../glassPatternsDefs';

// Function to draw pentagons with frames
// The pentagon fits within a rectangle of width w and height h
// It has 5 sides: oriented with one point at top or bottom
export function shapePentagon(svg, d, x, y, pentagons, labelTextOrOptions = null, subFrame = false, fullCorners = false, halfCorners = false,
  displayLabel = true, labelOptions = {}, drawFrame = true) {

  // Support both old positional style and new object style
  let labelText, options;
  if (typeof labelTextOrOptions === 'object' && labelTextOrOptions !== null && !Array.isArray(labelTextOrOptions)) {
    // New style: options object passed
    options = labelTextOrOptions;
    labelText = options.labelText ?? null;
    subFrame = options.subFrame ?? false;
    fullCorners = options.fullCorners ?? false;
    halfCorners = options.halfCorners ?? false;
    displayLabel = options.displayLabel ?? true;
    labelOptions = options.labelOptions ?? {};
    drawFrame = options.drawFrame ?? true;
  } else {
    // Old style: positional parameters
    labelText = labelTextOrOptions;
  }

  let currentX = x;
  let prev = {};

  const {
    fontSize = 14,
    fontColor = 'black',
    fontFamily = 'Arial',
    fontOpacity = 0.4,
  } = labelOptions;

  pentagons.forEach((pentagon, index) => {
    const {
      w, // width of bounding rectangle
      h = prev.h ?? 100, // height of bounding rectangle
      color = prev.color ?? 'white',
      glassType = prev.glassType || null,
      glassDesign = prev.glassDesign || null,
      frameWidth = prev.frameWidth ?? 0,
      frameColor = prev.frameColor ?? 'white',
      lineFrameColor = prev.lineFrameColor ?? frameColor,
      lineFrameThickness = prev.lineFrameThickness ?? 1,
      outerStrokeColor = prev.outerStrokeColor ?? 'black',
      outerStrokeWidth = prev.outerStrokeWidth ?? 1,
      orientation = prev.orientation ?? 'up', // 'up' or 'down'
      forceSubFrame = prev.forceSubFrame || false,
      fullCorners = prev.fullCorners || false,
      halfCorners = prev.halfCorners || false,
      displayLabel = prev.displayLabel ?? true
    } = pentagon;

    let baseFill = color;
    let tintOverlay = null;

    // TINTED GLASS
    if (glassType === 'Tinted') {
      baseFill = 'white';
      switch (glassDesign?.toLowerCase()) {
        case 'grey':
          tintOverlay = 'rgba(60, 60, 60, 0.2)';
          break;
        case 'bronze':
          tintOverlay = 'rgba(90, 50, 0, 0.2)';
          break;
        default:
          console.warn('Unknown tinted glass design:', glassDesign);
      }
    }

    // TEXTURED GLASS
    if (glassType === 'Textured' && glassDesign) {
      const patternId = `${glassDesign.toLowerCase().replace(/\s/g, '')}Pattern`;
      tintOverlay = `url(#${patternId})`;
      defineTexturedGlassPatterns(svg, glassDesign);
    }

    // Define pentagon points
    // Regular pentagon (all sides equal, like Pentagon building)
    // with flat bottom edge
    let innerPoints, outerPoints;

    const centerX = currentX + w / 2;

    // Use the smaller dimension to ensure pentagon fits in rectangle
    const radius = Math.min(w, h) / 2;

    // Position center so bottom edge of pentagon touches bottom of rectangle
    // For a regular pentagon with flat bottom, the distance from center to bottom edge
    // is radius * cos(54°)
    // Adjust by frameWidth to account for frame extension
    const centerY = y + h - radius * Math.cos(54 * Math.PI / 180) - frameWidth - 2*d + 1;

    // Regular pentagon with 5 equal sides
    // Start angle for flat bottom edge
    // 54° puts bottom-left point at correct position
    const startAngle = 54 * Math.PI / 180;

    // Calculate 5 points going clockwise, each 72° apart
    const basePoints = [];
    for (let i = 0; i < 5; i++) {
      const angle = startAngle + (i * 72 * Math.PI / 180);
      basePoints.push([
        centerX + radius * Math.cos(angle),
        centerY + radius * Math.sin(angle)
      ]);
    }

    // Apply frame extension to outer points
    outerPoints = basePoints.map((point, i) => {
      const nextI = (i + 1) % 5;
      const prevI = (i + 4) % 5;

      // Get vectors to adjacent points
      const v1 = [basePoints[prevI][0] - point[0], basePoints[prevI][1] - point[1]];
      const v2 = [basePoints[nextI][0] - point[0], basePoints[nextI][1] - point[1]];

      // Normalize
      const len1 = Math.hypot(v1[0], v1[1]);
      const len2 = Math.hypot(v2[0], v2[1]);
      v1[0] /= len1; v1[1] /= len1;
      v2[0] /= len2; v2[1] /= len2;

      // Bisector (average of normalized vectors)
      const bisector = [(v1[0] + v2[0]) / 2, (v1[1] + v2[1]) / 2];
      const bisectorLen = Math.hypot(bisector[0], bisector[1]);

      // Normalize bisector
      if (bisectorLen > 0.001) {
        bisector[0] /= bisectorLen;
        bisector[1] /= bisectorLen;
      }

      // Compute outward offset distance
      const cosHalfAngle = Math.abs(v1[0] * v2[0] + v1[1] * v2[1]);
      const sinHalfAngle = Math.sqrt(Math.max(0, 1 - cosHalfAngle * cosHalfAngle));
      const offset = frameWidth / Math.max(sinHalfAngle, 0.1);

      return [
        point[0] - bisector[0] * offset,
        point[1] - bisector[1] * offset
      ];
    });

    const P1 = outerPoints[0];
    const P2 = outerPoints[1];
    const P3 = outerPoints[2];
    const P4 = outerPoints[3];
    const P5 = outerPoints[4];

    // Helper: offset a line segment inward by distance d
    function offsetLine(P, Q, d) {
      const dx = Q[0] - P[0];
      const dy = Q[1] - P[1];
      const len = Math.hypot(dx, dy);

      // inward normal (perpendicular to the left)
      const nx = -dy / len;
      const ny = dx / len;

      return [
        [P[0] + nx * d, P[1] + ny * d],
        [Q[0] + nx * d, Q[1] + ny * d]
      ];
    }

    // Helper: intersection of two infinite lines
    function intersect(L1, L2) {
      const [A, B] = L1;
      const [C, D] = L2;

      const a1 = B[1] - A[1];
      const b1 = A[0] - B[0];
      const c1 = a1 * A[0] + b1 * A[1];

      const a2 = D[1] - C[1];
      const b2 = C[0] - D[0];
      const c2 = a2 * C[0] + b2 * C[1];

      const det = a1 * b2 - a2 * b1;

      return [
        (b2 * c1 - b1 * c2) / det,
        (a1 * c2 - a2 * c1) / det
      ];
    }

    // Offset each edge inward by frameWidth
    /*const E1 = offsetLine(P1, P2, frameWidth); // edge 1-2
    const E2 = offsetLine(P2, P3, frameWidth); // edge 2-3
    const E3 = offsetLine(P3, P4, frameWidth); // edge 3-4
    const E4 = offsetLine(P4, P5, frameWidth); // edge 4-5
    const E5 = offsetLine(P5, P1, frameWidth); // edge 5-1*/

    const E1 = offsetLine(P1, P2, frameWidth); // edge 1-2
    const E2 = offsetLine(P2, P3, frameWidth); // edge 2-3
    const E3 = offsetLine(P3, P4, frameWidth); // edge 3-4
    const E4 = offsetLine(P4, P5, frameWidth); // edge 4-5
    const E5 = offsetLine(P5, P1, frameWidth); // edge 5-1

    // Intersect offset edges to get inner pentagon
    const P1_inner = intersect(E5, E1);
    const P2_inner = intersect(E1, E2);
    const P3_inner = intersect(E2, E3);
    const P4_inner = intersect(E3, E4);
    const P5_inner = intersect(E4, E5);

    innerPoints = [P1_inner, P2_inner, P3_inner, P4_inner, P5_inner];

    // === OUTER FRAME ===
    if (drawFrame && frameWidth > 0) {
      const outerPointsStr = outerPoints.map(p => p.join(',')).join(' ');
      svg.append('polygon')
        .attr('points', outerPointsStr)
        .attr('fill', frameColor)
        .attr('stroke', lineFrameColor)
        .attr('stroke-width', lineFrameThickness);
    }

    // === BASE FILL ===
    const innerPointsStr = innerPoints.map(p => p.join(',')).join(' ');
    svg.append('polygon')
      .attr('points', innerPointsStr)
      .attr('fill', baseFill)
      .attr('stroke', outerStrokeColor)
      .attr('stroke-width', outerStrokeWidth);

    // === TINT OVERLAY ===
    if (tintOverlay) {
      svg.append('polygon')
        .attr('points', innerPointsStr)
        .attr('fill', tintOverlay)
        .attr('stroke', 'none');
    }

    // === SUBFRAME (inner border between frame and glass) ===
    if (drawFrame && frameWidth > 0) {
      // Draw the inner pentagon border (between frame and glass fill)
      svg.append('polygon')
        .attr('points', innerPointsStr)
        .attr('fill', 'none')
        .attr('stroke', lineFrameColor)
        .attr('stroke-width', lineFrameThickness);
    }

    // === LABEL ===
    const textToUse = labelText == null ? index + 1 : labelText;

    if (displayLabel) {
      // Position label at center of pentagon
      const centroidX = currentX + w / 2;
      const centroidY = y + h / 2;

      svg.append('text')
        .attr('x', centroidX)
        .attr('y', centroidY + fontSize / 3)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', fontSize)
        .attr('fill', fontColor)
        .attr('fill-opacity', fontOpacity)
        .attr('font-family', fontFamily)
        .text(textToUse);
    }

    currentX += w + 2 * frameWidth + d + 2 * lineFrameThickness;

    prev = {
      w, h, color,
      frameWidth, frameColor,
      lineFrameColor, lineFrameThickness,
      outerStrokeColor, outerStrokeWidth,
      orientation, fullCorners, halfCorners, displayLabel,
      glassType, glassDesign
    };
  });
}
